import { ComponentType, createElement, useCallback } from 'react';

import { CssVarsProvider } from '@mui/joy/styles';
import { flushSync } from 'react-dom';
import { Root, createRoot } from 'react-dom/client';

import { track } from '../../services/analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_closed,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../../services/analytics/events';
import { driver } from '../../services/driverjs';
import { theme } from '../../theme';
import { brand } from '../../theme/brand';

import {
  BACK_BUTTON_TEXT,
  CONTINUE_BUTTON_TEXT,
  FINISH_BUTTON_TEXT,
  TOUR_THEME_CSS_ID,
} from './constants';
import { HomeOnboardingPopover } from './popover/HomeOnboardingPopover';
import { TOUR_THEME_CSS } from './style';
import { runTourAction } from './tourActions';
import type { HomeOnboardingPopoverProps, OnboardingStep, TourEventProps } from './types';

/** Options common to runOnboardingTour + useOnboardingTour. `popoverComponent`
 *  lets a tour swap in a different popover surface (the queue tour uses
 *  QueueOnboardingPopover); it defaults to HomeOnboardingPopover so the home /
 *  unrated tours are untouched. */
export type OnboardingTourOptions = {
  hideBack?: boolean;
  popoverWidth?: number;
  lockScroll?: boolean;
  popoverComponent?: ComponentType<HomeOnboardingPopoverProps>;
  /** Per-tour backdrop overrides (default = the light home/unrated overlay).
   *  The queue tour uses a dark #121416 @ 0.25 dim + a 4px blur (Figma 363:46536). */
  overlayColor?: string;
  overlayOpacity?: number;
  overlayBlur?: number;
};

/** driver.js's own default, restored for steps that declare no override. */
const DEFAULT_POPOVER_OFFSET = 10;

export const hasVisibleOnboardingSteps = (steps: OnboardingStep[]): boolean =>
  steps.some((step) => step.show);

const tourHeaderText = (title?: string): string => (title ?? '').split(' ').slice(0, 3).join(' ');

const injectTourThemeStyle = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TOUR_THEME_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = TOUR_THEME_CSS_ID;
  style.textContent = TOUR_THEME_CSS;
  document.head.appendChild(style);
};

const SPOTLIGHT_ID = 'onboarding-spotlight';

/** Park a proxy over the union of `selector`'s matches and return its selector,
 *  so driver.js can cut out a region that no single element covers. Returns null
 *  when nothing matches, leaving the step on its own `element`. */
const SPOTLIT_CLASS = 'onboarding-spotlit';

const clearSpotlitTargets = () =>
  document
    .querySelectorAll(`.${SPOTLIT_CLASS}`)
    .forEach((el) => el.classList.remove(SPOTLIT_CLASS));

const placeSpotlight = (selector: string): string | null => {
  const targets = Array.from(document.querySelectorAll(selector)).filter(
    (el) => el.getBoundingClientRect().width > 0
  );
  clearSpotlitTargets();
  if (!targets.length) return null;
  targets.forEach((el) => el.classList.add(SPOTLIT_CLASS));
  const boxes = targets.map((el) => el.getBoundingClientRect());
  const left = Math.min(...boxes.map((b) => b.left));
  const top = Math.min(...boxes.map((b) => b.top));
  const right = Math.max(...boxes.map((b) => b.right));
  const bottom = Math.max(...boxes.map((b) => b.bottom));

  let proxy = document.getElementById(SPOTLIGHT_ID);
  if (!proxy) {
    proxy = document.createElement('div');
    proxy.id = SPOTLIGHT_ID;
    proxy.style.position = 'fixed';
    proxy.style.pointerEvents = 'none';
    proxy.style.zIndex = '0';
    document.body.appendChild(proxy);
  }
  proxy.style.left = `${left}px`;
  proxy.style.top = `${top}px`;
  proxy.style.width = `${right - left}px`;
  proxy.style.height = `${bottom - top}px`;
  return `#${SPOTLIGHT_ID}`;
};

const removeSpotlight = () => {
  clearSpotlitTargets();
  document.getElementById(SPOTLIGHT_ID)?.remove();
};

export const runOnboardingTour = (
  steps: OnboardingStep[],
  eventProps: TourEventProps,
  options: OnboardingTourOptions = {}
) => {
  const visibleSteps = steps.filter((step) => step.show);
  if (visibleSteps.length === 0) return;

  const PopoverComponent = options.popoverComponent ?? HomeOnboardingPopover;

  injectTourThemeStyle();
  // Queue tour: dark dim + blur (Figma). Scoped via an <html> class so the
  // home/unrated tours keep their light, no-blur overlay.
  if (options.overlayBlur) document.documentElement.classList.add('tour-overlay-blur');

  const isDarkMode = document.documentElement.getAttribute('data-joy-color-scheme') === 'dark';
  const overlayColor = options.overlayColor ?? (isDarkMode ? brand.blur.light : brand.blur.dark);
  const overlayOpacity = options.overlayOpacity ?? (isDarkMode ? 0.2 : 0.1);

  let popoverRoot: Root | null = null;
  let customContainer: HTMLDivElement | null = null;
  let interactiveTarget: HTMLElement | null = null;
  let interactiveHandler: ((event: Event) => void) | null = null;

  // While a step is highlighted, freeze scrolling so the user can't move the
  // highlighted element out from under driver.js's overlay cutout. Released
  // between steps (onDeselected) so driver.js can still scroll the next target
  // into view, then re-applied when the next popover renders.
  //
  // We freeze the highlighted element's actual scrollable ancestor(s) — the
  // filter drawer for the unrated tour, the app-shell #layout container for
  // the home tour (pages scroll inside #layout, not the window) — plus any
  // element explicitly tagged data-onboarding-scroll-lock. Detecting the real
  // scroll parent is what makes this work regardless of where the scroll lives.
  let scrollLocks: { el: HTMLElement; overflow: string; paddingRight: string }[] = [];
  const isScrollable = (el: HTMLElement): boolean => {
    const oy = getComputedStyle(el).overflowY;
    return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight;
  };
  const scrollableAncestors = (start: HTMLElement | null): HTMLElement[] => {
    const found: HTMLElement[] = [];
    let el = start?.parentElement ?? null;
    while (el && el !== document.body) {
      if (isScrollable(el)) found.push(el);
      el = el.parentElement;
    }
    return found;
  };
  const freeze = (el: HTMLElement) => {
    const prev = { el, overflow: el.style.overflow, paddingRight: el.style.paddingRight };
    // Compensate for the removed scrollbar so content doesn't shift sideways
    // (no-op on overlay-scrollbar platforms where the scrollbar has no width).
    const scrollbarWidth = el.offsetWidth - el.clientWidth;
    el.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const currentPad = parseFloat(getComputedStyle(el).paddingRight) || 0;
      el.style.paddingRight = `${currentPad + scrollbarWidth}px`;
    }
    return prev;
  };
  const lockHighlightScroll = (target: HTMLElement | null) => {
    if (!options.lockScroll || scrollLocks.length) return;
    const targets = new Set<HTMLElement>();
    // App-shell scroll container: every routed page scrolls inside #layout
    // (Layout.tsx, overflowY: scroll), not the window — this is what the home
    // tour needs frozen.
    const layout = document.getElementById('layout');
    if (layout) targets.add(layout);
    // Marked inner scroll containers (e.g. the filter drawer for the unrated
    // tour, which lives in a portal outside #layout).
    document
      .querySelectorAll<HTMLElement>('[data-onboarding-scroll-lock]')
      .forEach((el) => targets.add(el));
    // Any other scrollable ancestor of the highlighted element.
    scrollableAncestors(target).forEach((el) => targets.add(el));
    scrollLocks = Array.from(targets).map(freeze);
  };
  const unlockHighlightScroll = () => {
    scrollLocks.forEach(({ el, overflow, paddingRight }) => {
      el.style.overflow = overflow;
      el.style.paddingRight = paddingRight;
    });
    scrollLocks = [];
  };

  let lastShownIdx = -1;
  const stepEventProps = (idx: number) => {
    const step = visibleSteps[idx];
    // Per-step (number + header text) overlaid on top of the launcher-provided
    // context (platform_page, feature_name, fastpass_used, …). Order matters:
    // spread eventProps first so per-step values win for the shared keys, and
    // any extra launcher props (fastpass counts, etc.) ride along too.
    return {
      ...eventProps,
      tutorial_step_number: `Step ${idx + 1}`,
      tutorial_screen_header_text: tourHeaderText(step?.title),
    };
  };

  const detachInteractiveListener = () => {
    if (interactiveTarget && interactiveHandler) {
      interactiveTarget.removeEventListener('click', interactiveHandler);
    }
    interactiveTarget = null;
    interactiveHandler = null;
  };

  const attachInteractiveListener = (idx: number) => {
    detachInteractiveListener();
    const step = visibleSteps[idx];
    if (!step?.interactive || !step.element) return;
    const target = document.querySelector(step.element);
    if (!(target instanceof HTMLElement)) return;
    const handler = () => {
      if (idx === visibleSteps.length - 1) tour.destroy();
      else navigateTo(idx + 1, 'next');
    };
    target.addEventListener('click', handler);
    interactiveTarget = target;
    interactiveHandler = handler;
  };

  // Centralised transition helper: awaits the target step's beforeActions
  // (open drawer, expand accordion, etc.) BEFORE invoking the driver.js
  // navigation method, so driver.js measures against the resting DOM.
  const runNavigation = async (idx: number, kind: 'drive' | 'next' | 'prev') => {
    const step = visibleSteps[idx];
    // Run pre-step actions FIRST (open drawer, expand accordion, etc.) so
    // the DOM is mounted when driver.js queries for the target element.
    for (const key of step?.beforeActions ?? []) {
      await runTourAction(key);
    }
    // Diagnostic: warn if the target selector doesn't match anything — driver.js
    // silently centres the popover in the viewport when the target is missing,
    // which looks like the highlight is "wrong" but is really a selector miss.
    if (step?.element && !document.querySelector(step.element)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[onboarding] step ${idx + 1} target not found in DOM: ${step.element} — driver.js will skip the highlight stage.`
      );
    }
    // Release the scroll freeze only now — beforeActions (drawer open, accordion
    // expand) ran while frozen, and the freeze from the previous step / the
    // initial tour-start lock held until here. driver.js re-freezes in
    // onPopoverRender once it has scrolled the target into view.
    unlockHighlightScroll();
    // `popoverOffset` is a driver.js *config* option, not a per-step one, so a
    // step that declares one has to push it onto the config before driving.
    // Unset steps keep driver's default, so existing tours are untouched.
    const offset =
      typeof step?.popoverOffset === 'function' ? step.popoverOffset() : step?.popoverOffset;
    tour.setConfig({ ...tour.getConfig(), popoverOffset: offset ?? DEFAULT_POPOVER_OFFSET });
    // Measured after beforeActions so the list/drawer is already at rest.
    const spotlight = step?.spotlight ? placeSpotlight(step.spotlight) : null;
    if (!spotlight) removeSpotlight();
    tour.setConfig({
      ...tour.getConfig(),
      steps: driverSteps.map((driverStep, i) =>
        i === idx && spotlight ? { ...driverStep, element: spotlight } : driverStep
      ),
    });
    try {
      if (kind === 'drive') tour.drive();
      else if (kind === 'next') tour.moveNext();
      else tour.movePrevious();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[onboarding] driver.js navigation failed', { idx, kind, err });
    }
  };

  // Clicks that land while a step's beforeActions are still running (a slow drawer
  // mount, say) are dropped; otherwise driver.js moves twice and skips a step.
  let navigating = false;
  const navigateTo = async (idx: number, kind: 'drive' | 'next' | 'prev') => {
    if (navigating) return;
    navigating = true;
    try {
      await runNavigation(idx, kind);
    } finally {
      navigating = false;
    }
  };

  // Standalone green dot pinned to the highlight's corner facing the popover —
  // independent of driver.js's arrow (which we hide), so it's never thrown off
  // by floating-ui's overflow shifting of the popover.
  let floatingDot: HTMLDivElement | null = null;
  let floatingArrow: HTMLDivElement | null = null;
  const ensureDot = (): HTMLDivElement => {
    if (!floatingDot) {
      floatingDot = document.createElement('div');
      floatingDot.className = 'calyx-tour-dot';
      document.body.appendChild(floatingDot);
    }
    return floatingDot;
  };
  const ensureArrow = (): HTMLDivElement => {
    if (!floatingArrow) {
      floatingArrow = document.createElement('div');
      floatingArrow.className = 'calyx-tour-arrow';
      document.body.appendChild(floatingArrow);
    }
    return floatingArrow;
  };
  const removeOverlayBits = () => {
    if (floatingDot && floatingDot.parentNode) floatingDot.parentNode.removeChild(floatingDot);
    if (floatingArrow && floatingArrow.parentNode)
      floatingArrow.parentNode.removeChild(floatingArrow);
    floatingDot = null;
    floatingArrow = null;
  };
  const rectsOverlap = (a: DOMRect, b: DOMRect): boolean =>
    !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);

  const positionDotForStep = (idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return removeOverlayBits();
    const target = step.element ? document.querySelector(step.element) : null;
    if (!(target instanceof HTMLElement)) return removeOverlayBits();
    const t = target.getBoundingClientRect();
    const popover = document.querySelector<HTMLElement>('.driver-popover');
    const p = popover?.getBoundingClientRect();
    // If after driver.js's own scrollIntoView the popover still overlaps the
    // highlight (target is taller than the viewport, for example) drop the
    // visual cues — popover-on-highlight is its own indicator.
    if (p && rectsOverlap(p, t)) return removeOverlayBits();
    const dot = ensureDot();
    const arrow = ensureArrow();
    // Default placement: pick the axis the popover sits on relative to the
    // target via |dx| vs |dy| (PR #1031's original logic — works for the home
    // tour and any step where driver.js places the popover roughly on a single
    // axis from the target).
    //
    // When step.align is 'start' or 'end', we override: axis is taken from
    // step.side, and the arrow is pinned to the corresponding popover corner
    // (inset 28px) instead of being clamped to the popover/target centre. This
    // is what step 3 of the unrated tour needs (align='end' + side='top'
    // → arrow at popover bottom-right pointing down at the Done button).
    let dotX = t.left + t.width / 2;
    let dotY = t.top + t.height / 2;
    let arrowX = dotX;
    let arrowY = dotY;
    let arrowSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
    if (p) {
      const targetCx = t.left + t.width / 2;
      const targetCy = t.top + t.height / 2;
      const popoverCx = p.left + p.width / 2;
      const popoverCy = p.top + p.height / 2;
      const dx = popoverCx - targetCx;
      const dy = popoverCy - targetCy;
      const ARROW_CORNER_INSET = 28;
      const cornerAligned = step.align === 'start' || step.align === 'end';
      const verticalAxis = cornerAligned
        ? step.side === 'top' || step.side === 'bottom'
        : Math.abs(dy) >= Math.abs(dx);
      if (verticalAxis) {
        const popoverIsBelow = dy > 0;
        arrowSide = popoverIsBelow ? 'top' : 'bottom';
        arrowY = popoverIsBelow ? p.top : p.bottom;
        if (step.centerDot) {
          // Point the arrow at the (centered) dot so the two stay connected,
          // while the popover itself keeps its aligned placement.
          arrowX = Math.max(p.left + 12, Math.min(p.right - 12, targetCx));
        } else if (step.align === 'end') {
          arrowX = p.right - ARROW_CORNER_INSET;
        } else if (step.align === 'start') {
          arrowX = p.left + ARROW_CORNER_INSET;
        } else {
          arrowX = Math.max(t.left + 12, Math.min(t.right - 12, popoverCx));
        }
        dotY = popoverIsBelow ? t.bottom : t.top;
        dotX = step.centerDot
          ? targetCx
          : cornerAligned
            ? Math.max(t.left + 12, Math.min(t.right - 12, arrowX))
            : arrowX;
      } else {
        const popoverIsRight = dx > 0;
        arrowSide = popoverIsRight ? 'left' : 'right';
        arrowX = popoverIsRight ? p.left : p.right;
        if (step.centerDot) {
          // Point the arrow at the (centered) dot so the two stay connected,
          // while the popover itself keeps its aligned placement.
          arrowY = Math.max(p.top + 12, Math.min(p.bottom - 12, targetCy));
        } else if (step.align === 'end') {
          arrowY = p.bottom - ARROW_CORNER_INSET;
        } else if (step.align === 'start') {
          arrowY = p.top + ARROW_CORNER_INSET;
        } else {
          arrowY = Math.max(t.top + 12, Math.min(t.bottom - 12, popoverCy));
        }
        dotX = popoverIsRight ? t.right : t.left;
        dotY = step.centerDot
          ? targetCy
          : cornerAligned
            ? Math.max(t.top + 12, Math.min(t.bottom - 12, arrowY))
            : arrowY;
      }
    }
    dot.style.left = `${dotX}px`;
    dot.style.top = `${dotY}px`;
    arrow.style.left = `${arrowX}px`;
    arrow.style.top = `${arrowY}px`;
    arrow.dataset.side = arrowSide;
  };
  let lastIdx = 0;
  const repositionDot = () => positionDotForStep(lastIdx);
  window.addEventListener('resize', repositionDot);
  window.addEventListener('scroll', repositionDot, true);

  const mountReact = (popover: { wrapper: HTMLElement }, idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return;

    const wrapper = popover.wrapper;
    wrapper.style.padding = '0';
    wrapper.style.maxWidth = 'none';
    wrapper.style.width = 'auto';
    wrapper.style.background = 'transparent';
    wrapper.style.boxShadow = 'none';
    wrapper.style.borderRadius = '10px';
    // Hide driver.js's default popover chrome but keep the arrow — the green
    // dot is positioned independently (anchored to the highlight) so it stays
    // accurate even when floating-ui shifts the popover to fit the viewport.
    Array.from(wrapper.children).forEach((child) => {
      if (child === customContainer) return;
      const el = child as HTMLElement;
      if (el.classList.contains('driver-popover-arrow')) return;
      el.style.display = 'none';
    });

    if (customContainer && customContainer.parentNode !== wrapper) {
      wrapper.appendChild(customContainer);
    }
    if (!customContainer) {
      customContainer = document.createElement('div');
      wrapper.appendChild(customContainer);
      popoverRoot = createRoot(customContainer);
    }

    flushSync(() => {
      popoverRoot?.render(
        createElement(
          CssVarsProvider,
          { theme, defaultMode: 'light' },
          createElement(PopoverComponent, {
            title: step.title,
            body: step.body,
            customContent: step.customContent,
            stepIndex: idx,
            totalSteps: visibleSteps.length,
            interactive: step.interactive,
            hideBack: options.hideBack,
            popoverWidth: options.popoverWidth,
            primaryLabel: step.primaryLabel,
            onBack: () => {
              track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text: BACK_BUTTON_TEXT,
              });
              if (idx > 0) navigateTo(idx - 1, 'prev');
            },
            onContinue: () => {
              track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text:
                  idx === visibleSteps.length - 1 ? FINISH_BUTTON_TEXT : CONTINUE_BUTTON_TEXT,
              });
              if (idx === visibleSteps.length - 1) tour.destroy();
              else navigateTo(idx + 1, 'next');
            },
            onClose: () => {
              track(EVNT_tutorial_screen_closed, EVENT_TUTORIAL, stepEventProps(idx));
              tour.destroy();
            },
          })
        )
      );
    });
  };

  const hasInteractiveStep = visibleSteps.some((step) => step.interactive);

  const driverSteps = visibleSteps.map((s) => ({
    // Omit `element` for centred modal steps — driver.js centres the popover
    // in the viewport when a step has no target.
    ...(s.element ? { element: s.element } : {}),
    popover: { side: s.side, align: s.align ?? 'center', title: '', description: '' },
  }));

  const tour = driver({
    showProgress: false,
    showButtons: [],
    allowClose: !hasInteractiveStep,
    smoothScroll: false,
    overlayColor,
    overlayOpacity,
    popoverClass: 'driverjs-onboarding-theme',
    onDestroyed: () => {
      removeSpotlight();
      detachInteractiveListener();
      removeOverlayBits();
      unlockHighlightScroll();
      document.documentElement.classList.remove('tour-overlay-blur');
      window.removeEventListener('resize', repositionDot);
      window.removeEventListener('scroll', repositionDot, true);
      const root = popoverRoot;
      popoverRoot = null;
      customContainer = null;
      if (root) queueMicrotask(() => root.unmount());
    },
    // Fires when the current step's highlight is removed (between steps).
    // Hide the dot + arrow during the transition gap so they don't linger at
    // the old step's coordinates while the new popover renders.
    onDeselected: () => {
      detachInteractiveListener();
      removeOverlayBits();
      // Release the scroll freeze during the transition so driver.js can scroll
      // the next step's target into view; re-applied in onPopoverRender.
      unlockHighlightScroll();
    },
    onPopoverRender: (popover, { state }) => {
      const idx = state.activeIndex ?? 0;
      // PGV once per step (onPopoverRender may fire on re-render).
      if (idx !== lastShownIdx) {
        lastShownIdx = idx;
        track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, stepEventProps(idx));
      }
      mountReact(popover, idx);
      attachInteractiveListener(idx);
      lastIdx = idx;
      // Position the floating dot on the next frame so the popover has settled
      // its final rect (we read it to decide which edge of the highlight to
      // anchor the dot to). driver.js has already scrolled the target into view
      // by now, so freeze the drawer scroll to pin the highlight in place.
      requestAnimationFrame(() => {
        positionDotForStep(idx);
        const step = visibleSteps[idx];
        lockHighlightScroll(
          step?.element ? document.querySelector<HTMLElement>(step.element) : null
        );
      });
    },
    steps: driverSteps,
  });

  // Freeze scrolling immediately — before step 1's beforeActions (open drawer,
  // expand accordion) and driver.js's first drive — so the user can't scroll
  // the page during that opening gap and break the highlight's positioning.
  lockHighlightScroll(null);
  navigateTo(0, 'drive');
};

export const useOnboardingTour = (
  steps: OnboardingStep[],
  eventProps: TourEventProps,
  options: OnboardingTourOptions = {}
) => {
  const canShow = hasVisibleOnboardingSteps(steps);
  const start = useCallback(
    () => runOnboardingTour(steps, eventProps, options),
    [steps, eventProps, options]
  );
  return { canShow, start };
};
